"use client";

import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import { twMerge } from "tailwind-merge";
import { usePostHog } from "posthog-js/react";

type Props = {
  priceText: string;
  priceSuffix: string;
  ctaText: string;
  sourceUrl: string;
  sourceImage: string;
  sourceName: string;
  listingId: string;
  /** Element id of the inline action panel — the bar hides while it is visible. */
  watchTargetId: string;
};

const ListingMobileCTA: React.FC<Props> = ({
  priceText,
  priceSuffix,
  ctaText,
  sourceUrl,
  sourceImage,
  sourceName,
  listingId,
  watchTargetId,
}) => {
  const posthog = usePostHog();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = document.getElementById(watchTargetId);
    if (!target || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting);
      },
      { rootMargin: "0px 0px -56px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [watchTargetId]);

  return (
    <div
      className={twMerge(
        "fixed inset-x-0 bottom-0 z-40 flex flex-row items-center gap-3 border-t border-border bg-background px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-16px_rgba(24,24,27,0.35)] transition-transform duration-300 lg:hidden",
        !visible && "translate-y-full",
      )}
    >
      <div className="min-w-0">
        <p className="truncate text-base font-bold leading-tight text-foreground">
          {priceText}
        </p>
        <p className="truncate text-[11px] leading-tight text-muted-foreground">
          {priceSuffix}
        </p>
      </div>
      <a
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={ctaText}
        onClick={() =>
          posthog.capture("listing_source_click", {
            listing_id: listingId,
            source: sourceName,
            placement: "mobile_bar",
          })
        }
        className="flex h-11 flex-1 flex-row items-center justify-center gap-2 rounded-xl bg-secondary px-4 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80"
      >
        <Image
          src={sourceImage}
          alt={sourceName}
          width={100}
          height={100}
          sizes={IMAGE_SIZES.icon32}
          className="size-5 rounded-full border border-black/10 bg-white"
        />
        <span className="truncate">{ctaText}</span>
        <ArrowUpRight className="size-4 min-w-4" />
      </a>
    </div>
  );
};

export default ListingMobileCTA;
